import {
  FETCH_OFFERS,
  SET_SELECTED_OFFER,
  SET_OTP_VERIFIED,
  SET_APP_PHASE,
  SET_LOADING,
  SET_ERROR,
  RESET_APPLICATION,
  SET_CANDIDATE_EMAIL,
} from "./mutation-types";

import service from "@/service";
import config from "@/config";
import axios from "axios";

const $service = service(axios, config);

/** Délai max d'envoi de la candidature (les documents sont déjà téléversés). */
const SUBMIT_TIMEOUT_MS = 120000;

/**
 * Transforme n'importe quelle erreur axios en { msg, status, code, reference }.
 *
 * Avant, le message générique (« Erreur lors de l'envoi. ») s'affichait dès que
 * la réponse n'était pas un JSON de l'API — c'est-à-dire exactement dans les
 * cas où l'API n'a jamais vu la requête ou n'a pas pu répondre :
 *   - connexion coupée / délai axios dépassé (pas de error.response) ;
 *   - 413 de Nginx (client_max_body_size, 1 Mo par défaut) : page HTML ;
 *   - 502 / 504 de Nginx (API arrêtée, SMTP trop lent…) : page HTML.
 */
export function toApiError(error, fallback) {
  const res = error && error.response;

  if (!res) {
    const isTimeout =
      error &&
      (error.code === "ECONNABORTED" ||
        error.code === "ETIMEDOUT" ||
        /timeout/i.test(error.message || ""));
    return {
      msg: isTimeout
        ? "Le serveur met trop de temps à répondre (connexion lente ?). Vérifiez votre connexion internet puis réessayez."
        : "Impossible de joindre le serveur. Vérifiez votre connexion internet puis réessayez.",
      status: 0,
      code: isTimeout ? "TIMEOUT" : "NETWORK_ERROR",
      reference: null,
    };
  }

  const data = res.data && typeof res.data === "object" ? res.data : {};
  let msg = Array.isArray(data.message)
    ? data.message.join(" ")
    : typeof data.message === "string"
    ? data.message
    : "";

  // Message technique anglais d'une ancienne version de l'API
  if (/^internal server error$/i.test(msg)) msg = "";

  if (!msg) {
    // Réponse non-JSON (page HTML de Nginx, proxy, etc.)
    if (res.status === 413) {
      msg =
        "Ce fichier est trop volumineux pour être envoyé. Réduisez sa taille (PDF compressé) puis réessayez.";
    } else if (res.status === 502 || res.status === 503) {
      msg =
        "Le service est momentanément indisponible. Merci de réessayer dans quelques minutes.";
    } else if (res.status === 504) {
      msg =
        "Le serveur a mis trop de temps à répondre. Merci de réessayer dans quelques minutes.";
    } else if (res.status >= 500) {
      msg =
        "Une erreur technique est survenue de notre côté. Merci de réessayer dans quelques minutes.";
    } else if (res.status === 429) {
      msg = "Trop de tentatives. Merci de patienter quelques minutes.";
    } else {
      msg = `${fallback} (code ${res.status})`;
    }
  }

  return {
    msg,
    status: res.status,
    code: typeof data.code === "string" ? data.code : null,
    reference: data.reference || null,
  };
}

export default {
  // ─── Charger les offres actives (landing page) ──────────────────────────────
  async applyGetActiveOffers({ commit }) {
    commit(SET_LOADING, { key: "offers", value: true });
    commit(SET_ERROR, { key: "offers", message: "" });

    const result = await $service
      .getActiveOffers()
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) => toApiError(error, "Erreur de chargement des offres."));

    commit(SET_LOADING, { key: "offers", value: false });

    if (result.status === 200) {
      commit(FETCH_OFFERS, result.data);
      return result;
    }

    commit(SET_ERROR, { key: "offers", message: result.msg });
    return { ...result, status: 400 };
  },

  // ─── Sélectionner une offre ──────────────────────────────────────────────────
  applySelectOffer({ commit }, offer) {
    commit(SET_SELECTED_OFFER, offer);
    commit(RESET_APPLICATION);
  },

  // ─── Restaurer session depuis un brouillon local (skip OTP) ─────────────────
  // Appelé depuis CareersPage quand un brouillon récemment vérifié est trouvé
  applyRestoreDraftSession({ commit }, { email, offerId }) {
    commit(SET_CANDIDATE_EMAIL, email);
    commit(SET_OTP_VERIFIED, { verified: true, email });
    commit(SET_APP_PHASE, "form");
    commit(SET_ERROR, { key: "email", message: "" });
    commit(SET_ERROR, {
      key: "offerId",
      message: offerId ? "" : "Offre introuvable.",
    });
    commit(SET_ERROR, { key: "otp", message: "" });
    commit(SET_ERROR, { key: "submit", message: "" });
  },

  // ─── NOUVEAU : renvoyer vers la vérification e-mail sans perdre le formulaire
  // (vérification expirée côté serveur, ex. brouillon repris le lendemain)
  applyRequireReverification({ commit }, { email, message }) {
    commit(SET_OTP_VERIFIED, { verified: false, email });
    commit(SET_CANDIDATE_EMAIL, email);
    commit(SET_ERROR, { key: "submit", message: "" });
    commit(SET_ERROR, { key: "otp", message: "" });
    commit(SET_ERROR, { key: "email", message: message || "" });
    commit(SET_APP_PHASE, "email");
  },

  // ─── Envoyer le code OTP ────────────────────────────────────────────────────
  async applySendOtp({ commit }, { email, offerId }) {
    commit(SET_LOADING, { key: "sendOtp", value: true });
    commit(SET_ERROR, { key: "email", message: "" });

    const cleanEmail = (email || "").trim();
    const result = await $service
      .sendOtp({ email: cleanEmail, offerId })
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) => toApiError(error, "Impossible d'envoyer le code."));

    commit(SET_LOADING, { key: "sendOtp", value: false });
    commit(SET_CANDIDATE_EMAIL, cleanEmail);

    if (result.status === 200) {
      commit(SET_APP_PHASE, "otp");
      return result;
    }

    // Afficher le message d'erreur (limite horaire, offre clôturée, SMTP…)
    commit(SET_ERROR, { key: "email", message: result.msg });
    return { ...result, httpStatus: result.status, status: 400 };
  },

  // ─── Vérifier le code OTP ───────────────────────────────────────────────────
  async applyVerifyOtp({ commit }, { email, offerId, code }) {
    commit(SET_LOADING, { key: "verifyOtp", value: true });
    commit(SET_ERROR, { key: "otp", message: "" });

    const result = await $service
      .verifyOtp({ email, offerId, code })
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) => toApiError(error, "Code invalide ou expiré."));

    commit(SET_LOADING, { key: "verifyOtp", value: false });

    if (result.status === 200) {
      commit(SET_OTP_VERIFIED, { verified: true, email });
      commit(SET_APP_PHASE, "form");
      return result;
    }

    // Afficher l'erreur avec nb de tentatives restantes si disponible
    commit(SET_ERROR, { key: "otp", message: result.msg });
    return { ...result, httpStatus: result.status, status: 400 };
  },

  // ─── Soumettre la candidature ────────────────────────────────────────────────
  // Les documents sont téléversés dès leur sélection (POST /applications/files) :
  // on n'envoie ici que leurs identifiants, plus le jeton de session remis
  // par /otp/verify. `files` reste accepté (fichiers joints directement).
  async applySubmitApplication(
    { commit, state },
    { form, files = {}, stagedFileIds = [], sessionToken }
  ) {
    commit(SET_LOADING, { key: "submit", value: true });
    commit(SET_ERROR, { key: "submit", message: "" });

    const fd = new FormData();
    fd.append("offerId", state.selectedOffer.id);
    fd.append("email", (state.candidateEmail || "").trim());
    if (sessionToken) fd.append("sessionToken", sessionToken);
    if (stagedFileIds.length)
      fd.append("stagedFileIds", JSON.stringify(stagedFileIds));
    fd.append("firstName", (form.firstName || "").trim());
    fd.append("lastName", (form.lastName || "").trim());
    fd.append("phone", form.phone);
    fd.append("consentData", String(!!form.consentData));
    fd.append("consentFuture", String(!!form.consentFuture));
    fd.append("consentTerms", String(!!form.consentTerms));
    if (form.linkedin) fd.append("linkedin", form.linkedin.trim());
    if (form.message) fd.append("message", form.message);

    // Fichiers standards
    if (files.cv) fd.append("cv", files.cv, files.cv.name);
    if (files.motivation)
      fd.append("motivation", files.motivation, files.motivation.name);

    // Autres fichiers joints directement (s'il y en a)
    Object.keys(files).forEach((key) => {
      if (key === "cv" || key === "motivation") return;
      const f = files[key];
      if (f) fd.append(key, f, f.name);
    });

    // Champs supplémentaires : fusion « Autre (préciser) »
    if (form.extraFields && Object.keys(form.extraFields).length > 0) {
      const mergedExtraFields = {};

      Object.keys(form.extraFields).forEach((key) => {
        // Ignorer les clés _other (elles ne sont pas des champs réels)
        if (key.endsWith("_other")) return;

        const rawValue = form.extraFields[key];

        if (
          rawValue === "Autre (préciser)" &&
          form.otherFields &&
          (form.otherFields[key] || "").trim()
        ) {
          mergedExtraFields[key] = form.otherFields[key].trim();
        } else if (typeof rawValue === "boolean") {
          // CORRECTIF : les cases à cocher partent en "true"/"false" (texte),
          // format attendu par le serveur (avant : `true` booléen → refusé
          // comme « champ requis » alors que la case était cochée).
          mergedExtraFields[key] = rawValue ? "true" : "false";
        } else {
          mergedExtraFields[key] = rawValue;
        }
      });

      fd.append("extraFieldsJson", JSON.stringify(mergedExtraFields));
    }

    const result = await $service
      // 2e argument = config axios : à transmettre dans ton service (voir README)
      .submitApplication(fd, { timeout: SUBMIT_TIMEOUT_MS })
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) => toApiError(error, "Erreur lors de l'envoi."));

    commit(SET_LOADING, { key: "submit", value: false });

    if (result.status === 201 || result.status === 200) {
      commit(SET_APP_PHASE, "success");
      return result;
    }

    // La vérification e-mail est gérée par le composant (changement d'étape) :
    // inutile d'afficher le message en bas du formulaire.
    if (result.code !== "EMAIL_VERIFICATION_REQUIRED") {
      commit(SET_ERROR, { key: "submit", message: result.msg });
    }
    return { ...result, httpStatus: result.status, status: 400 };
  },

  // ─── NOUVEAU : documents téléversés dès leur sélection ──────────────────────
  // Pas de mutation : l'état d'envoi (progression, erreurs) est local au
  // composant. Chaque action renvoie { status, data } ou { status, msg, code }.

  async applyUploadApplicationFile(_, { formData, onProgress }) {
    return $service
      .uploadApplicationFile(formData, onProgress)
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) => toApiError(error, "L'envoi du fichier a échoué."));
  },

  async applyListApplicationFiles(_, { email, offerId, sessionToken }) {
    return $service
      .listApplicationFiles({ email, offerId, sessionToken })
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) =>
        toApiError(error, "Impossible de récupérer vos documents.")
      );
  },

  async applyDeleteApplicationFile(_, { id, email, offerId, sessionToken }) {
    return $service
      .deleteApplicationFile(id, { email, offerId, sessionToken })
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) =>
        toApiError(error, "Impossible de retirer le document.")
      );
  },

  // ─── NOUVEAU : brouillon serveur et déconnexion de l'appareil ──────────────
  async applyLoadApplicationDraft(_, { email, offerId, sessionToken }) {
    return $service
      .loadApplicationDraft({ email, offerId, sessionToken })
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) =>
        toApiError(error, "Impossible de récupérer vos réponses.")
      );
  },

  async applySaveApplicationDraft(_, { email, offerId, sessionToken, data }) {
    return $service
      .saveApplicationDraft({ email, offerId, sessionToken, data })
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) =>
        toApiError(error, "Impossible de sauvegarder vos réponses.")
      );
  },

  async applyLogoutCandidate({ commit }, { email, offerId, sessionToken }) {
    const result = await $service
      .logoutCandidate({ email, offerId, sessionToken })
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) => toApiError(error, "Déconnexion impossible."));
    // Côté navigateur, on repart de zéro quoi qu'il arrive
    commit(RESET_APPLICATION);
    return result;
  },

  // ─── Envois de code restants (remplace l'appel axios direct du composant) ──
  async applyGetOtpRateLimit(_, { email, offerId }) {
    return $service
      .getOtpRateLimit({ email: (email || "").trim(), offerId })
      .then((res) => ({ data: res.data, status: res.status }))
      .catch((error) => toApiError(error, "Limite indisponible."));
  },

  // ─── Réinitialiser le flux ───────────────────────────────────────────────────
  applyResetApplication({ commit }) {
    commit(RESET_APPLICATION);
  },
};
